import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { login, resetPassword } from "../services/authService";
import LogoMark from "../components/LogoMark";
import styles from "./AdminLogin.module.css";

function AdminLogin() {
  const [mode, setMode] = useState("login"); // "login" | "reset"

  // login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // reset state
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError("Wrong email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  const switchToReset = () => {
    setResetEmail(email);
    setResetSent(false);
    setResetError("");
    setMode("reset");
  };

  const switchToLogin = () => {
    setError("");
    setMode("login");
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetSubmitting(true);
    try {
      await resetPassword(resetEmail.trim());
      setResetSent(true);
    } catch (err) {
      // Don't reveal whether the email exists — same success message
      // either way, except for genuine problems like a malformed
      // address or a network/rate-limit issue.
      if (err.code === "auth/user-not-found") {
        setResetSent(true);
      } else if (err.code === "auth/invalid-email") {
        setResetError("That doesn't look like a valid email address.");
      } else {
        setResetError("Couldn't send the reset email — please try again shortly.");
      }
    } finally {
      setResetSubmitting(false);
    }
  };

  if (mode === "reset") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.logo}>
            <LogoMark />
            BOI Gardens &amp; Car Cleaning
          </div>
          <h1>Reset password</h1>

          {resetSent ? (
            <>
              <p className={styles.helperText}>
                If an account exists for that email, a reset link is on its
                way — check your inbox (and spam folder).
              </p>
              <button
                type="button"
                className={`btn btnGhost ${styles.submit}`}
                onClick={switchToLogin}
              >
                Back to sign in
              </button>
            </>
          ) : (
            <form onSubmit={handleReset}>
              <p className={styles.helperText}>
                Enter your email and we'll send you a link to reset your
                password.
              </p>

              <div className={styles.field}>
                <label htmlFor="resetEmail">Email</label>
                <input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>

              {resetError && <div className={styles.errorText}>{resetError}</div>}

              <button
                type="submit"
                className={`btn btnPrimary ${styles.submit}`}
                disabled={resetSubmitting}
              >
                {resetSubmitting ? "Sending…" : "Send reset link"}
              </button>

              <button
                type="button"
                className={styles.linkBtn}
                onClick={switchToLogin}
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit}>
        <div className={styles.logo}>
          <LogoMark />
          BOI Gardens &amp; Car Cleaning
        </div>
        <h1>Staff login</h1>

        <div className={styles.field}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {error && <div className={styles.errorText}>{error}</div>}

        <button type="submit" className={`btn btnPrimary ${styles.submit}`} disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <button type="button" className={styles.linkBtn} onClick={switchToReset}>
          Forgot password?
        </button>
      </form>
    </div>
  );
}

export default AdminLogin;
